// Advanced Academics view — a chronological academic-year timeline.
// Events are grouped by semester and drawn on a vertical spine (metro-line feel),
// colour-coded by type, with a live TODAY marker, a "next event" countdown hero,
// undated milestone connectors, and a show/hide-past toggle.
// Shared body rendered inside either shell (desktop / mobile via `mode`).

import { useMemo, useState } from 'react';
import {
  Calendar, Download, FileText, AlertCircle, GraduationCap,
  History, ChevronDown, ChevronUp,
} from 'lucide-react';
import { T, MonoLabel, EmptyState } from '@/components/dashboard/DashboardShell';
import {
  CalendarEvent, EVENT_TYPES, useAcademicEvents, categorizeEvents,
  exportToICS, eventAccent, parseDate, formatDate,
} from '@/hooks/useAcademicCalendar';

type ShellComponent = React.ComponentType<{
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  children: React.ReactNode;
}>;

function eventIcon(type: string, size: number, color: string) {
  switch (type) {
    case 'exam': return <FileText size={size} color={color} />;
    case 'holiday': return <Calendar size={size} color={color} />;
    case 'deadline': return <AlertCircle size={size} color={color} />;
    case 'event': return <GraduationCap size={size} color={color} />;
    default: return <Calendar size={size} color={color} />;
  }
}

function daysUntilExact(dateStr: string): number | null {
  const d = parseDate(dateStr);
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function countdownLabel(n: number): string {
  if (n < 0) return 'Passed';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n <= 30) return `In ${n} days`;
  if (n <= 45) return 'In ~1 month';
  return `In ${Math.round(n / 30)} months`;
}

export function AcademicCalendarContent({ Shell, mode }: { Shell: ShellComponent; mode: 'desktop' | 'mobile' }) {
  const { events, isLoading } = useAcademicEvents();
  const [selectedType, setSelectedType] = useState('all');
  const [showPast, setShowPast] = useState(false);

  const filtered = useMemo(
    () => events.filter(e => selectedType === 'all' || e.type === selectedType),
    [events, selectedType],
  );
  const { upcoming, past } = useMemo(() => categorizeEvents(filtered), [filtered]);

  // Events shown in the timeline
  const visible = showPast ? filtered : upcoming;
  const pastIds = useMemo(() => new Set(past.map(e => e.id)), [past]);
  const firstUpcomingId = upcoming.find(e => parseDate(e.startDate))?.id ?? upcoming[0]?.id ?? null;

  // Group visible events by semester, preserving order
  const groups = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of visible) {
      const k = e.program || 'Academic Calendar';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    }
    return Array.from(m.entries());
  }, [visible]);

  const todayLabel = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const badge = events.length > 0 ? (
    <span style={{
      fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', color: T.teal,
      background: T.tealBg, border: `1px solid ${T.tealBorder}`, borderRadius: 4,
      padding: mode === 'desktop' ? '3px 9px' : '2px 7px',
    }}>{mode === 'desktop' ? `${upcoming.length} UPCOMING` : upcoming.length}</span>
  ) : undefined;

  const exportBtn = mode === 'desktop' ? (
    <button
      onClick={() => exportToICS(events, selectedType)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10,
        background: T.surface, border: `1px solid ${T.border}`, color: T.text50,
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', cursor: 'pointer', transition: '.15s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = T.surfaceHov)}
      onMouseLeave={e => (e.currentTarget.style.background = T.surface)}
    >
      <Download size={13} /> EXPORT .ICS
    </button>
  ) : (
    <button
      onClick={() => exportToICS(events, selectedType)}
      style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: T.surface, border: `1px solid ${T.border}`, color: T.text50,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      }}
      aria-label="Export calendar"
    >
      <Download size={16} />
    </button>
  );

  return (
    <Shell
      title="Academics"
      subtitle={mode === 'desktop' ? 'Academic year calendar & key dates' : 'Academic calendar'}
      loading={isLoading}
      badge={badge}
      actions={events.length > 0 ? exportBtn : undefined}
    >
      <style>{`
        @keyframes acPulse { 0%,100% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.25); opacity: .6 } }
        .acEvRow:hover .acEvTitle { color: #fff !important; }
      `}</style>

      {/* ── Filters + past toggle ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {EVENT_TYPES.map(type => {
          const active = selectedType === type.value;
          return (
            <button
              key={type.value}
              onClick={() => setSelectedType(type.value)}
              style={{
                padding: mode === 'desktop' ? '8px 16px' : '8px 14px', borderRadius: 9, flexShrink: 0,
                background: active ? T.tealBg : T.surface,
                border: `1px solid ${active ? T.tealBorder : T.border}`,
                color: active ? T.teal : T.text50,
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
                cursor: 'pointer', transition: '.15s', whiteSpace: 'nowrap',
              }}
            >{type.label.toUpperCase()}</button>
          );
        })}
        {past.length > 0 && (
          <button
            onClick={() => setShowPast(s => !s)}
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 14px', borderRadius: 9, flexShrink: 0,
              background: showPast ? 'rgba(255,255,255,.06)' : T.surface,
              border: `1px solid ${showPast ? T.borderHov : T.border}`,
              color: showPast ? T.text : T.text50,
              fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em', cursor: 'pointer', transition: '.15s',
            }}
          >
            <History size={12} />
            {showPast ? 'HIDE PAST' : `SHOW PAST · ${past.length}`}
            {showPast ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>

      {/* ── Timeline ─────────────────────────────────────────────────────── */}
      {groups.length === 0 ? (
        <EmptyState icon={Calendar} text={selectedType === 'all' ? 'NO UPCOMING EVENTS' : 'NO EVENTS MATCH THIS FILTER'} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          {groups.map(([semester, semEvents], gi) => (
            <SemesterBlock
              key={semester}
              semester={semester}
              events={semEvents}
              pastIds={pastIds}
              firstUpcomingId={firstUpcomingId}
              todayLabel={todayLabel}
              first={gi === 0}
            />
          ))}
        </div>
      )}
    </Shell>
  );
}

// ─── Semester block ──────────────────────────────────────────────────────────────
function SemesterBlock({ semester, events, pastIds, firstUpcomingId, todayLabel, first }: {
  semester: string; events: CalendarEvent[]; pastIds: Set<string>;
  firstUpcomingId: string | null; todayLabel: string; first: boolean;
}) {
  // Date span from dated events
  const dates = events.map(e => parseDate(e.startDate)).filter((d): d is Date => !!d).sort((a, b) => a.getTime() - b.getTime());
  const span = dates.length
    ? `${dates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${dates[dates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';

  return (
    <div>
      {/* Semester header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, letterSpacing: '-.01em', margin: 0 }}>{semester}</h2>
          <MonoLabel size={8}>{events.length} EVENTS</MonoLabel>
        </div>
        {span && <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.06em', color: T.text35, marginTop: 4 }}>{span}</div>}
      </div>

      {/* Timeline spine */}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 15, top: 12, bottom: 12, width: 2, background: 'rgba(255,255,255,.07)' }} />
        {events.map((event, i) => {
          const showToday = first && event.id === firstUpcomingId;
          return (
            <div key={event.id}>
              {showToday && <TodayMarker label={todayLabel} />}
              <EventNode event={event} isPast={pastIds.has(event.id)} last={i === events.length - 1} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Today marker ────────────────────────────────────────────────────────────────
function TodayMarker({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative', zIndex: 2, padding: '2px 0 16px' }}>
      <div style={{ width: 32, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        <div style={{
          width: 12, height: 12, borderRadius: '50%', background: T.teal,
          border: `2px solid ${T.bg}`, boxShadow: '0 0 10px rgba(20,184,166,.7)',
          animation: 'acPulse 2.2s ease-in-out infinite',
        }} />
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', color: T.teal, whiteSpace: 'nowrap' }}>
          TODAY · {label.toUpperCase()}
        </span>
        <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(20,184,166,.5), transparent)' }} />
      </div>
    </div>
  );
}

// ─── Event node ──────────────────────────────────────────────────────────────────
function EventNode({ event, isPast, last }: { event: CalendarEvent; isPast: boolean; last: boolean }) {
  const accent = eventAccent(event.type);
  const dated = !!parseDate(event.startDate);
  const days = dated && !isPast ? daysUntilExact(event.startDate) : null;
  const hasDesc = event.description && event.description !== event.startDate;

  return (
    <div className="acEvRow" style={{
      display: 'flex', gap: 14, position: 'relative', zIndex: 1,
      opacity: isPast ? 0.5 : 1, paddingBottom: last ? 0 : 18,
    }}>
      {/* Node */}
      <div style={{ width: 32, display: 'flex', justifyContent: 'center', flexShrink: 0, paddingTop: dated ? 0 : 6 }}>
        {dated ? (
          <div style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            background: `${accent}1f`, border: `1px solid ${accent}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isPast ? 'none' : `0 0 0 4px ${T.bg}`,
          }}>
            {eventIcon(event.type, 14, accent)}
          </div>
        ) : (
          <div style={{ width: 11, height: 11, borderRadius: '50%', background: T.bg, border: `2px solid ${accent}66` }} />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, paddingTop: dated ? 1 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <h3 className="acEvTitle" style={{
            fontSize: dated ? 13.5 : 12, fontWeight: dated ? 500 : 400,
            color: dated ? T.text : T.text50, margin: 0, lineHeight: 1.3, transition: 'color .15s',
          }}>{event.title}</h3>
          {days != null && (
            <span style={{
              fontFamily: T.mono, fontSize: 8, letterSpacing: '.05em', whiteSpace: 'nowrap', flexShrink: 0,
              color: accent, background: `${accent}1a`, border: `1px solid ${accent}40`, borderRadius: 4, padding: '2px 6px',
            }}>{countdownLabel(days)}</span>
          )}
        </div>

        {hasDesc && (
          <p style={{ fontSize: 11, color: T.text35, margin: '5px 0 0', lineHeight: 1.5, fontWeight: 300 }}>
            {event.description}
          </p>
        )}

        {dated && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 7 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.text50 }}>
              <Calendar size={11} color={accent} />
              {formatDate(event.startDate)}{event.endDate && ` – ${formatDate(event.endDate)}`}
            </span>
            <span style={{
              fontFamily: T.mono, fontSize: 8, letterSpacing: '.08em', color: accent,
              background: `${accent}14`, border: `1px solid ${accent}2e`, borderRadius: 4, padding: '2px 6px',
            }}>{event.type.toUpperCase()}</span>
          </div>
        )}
      </div>
    </div>
  );
}
